import {
    // Import the Props interfaces from the correct path
    SubsectionItem as BaseSubsectionItem,
    StandaloneItem as BaseStandaloneItem,
    ProcessedSectionContentItem,
    PrimitiveContentBlockProps as PrimitiveContentBlock,
    TextContentProps as TextContentObject,
    TableContentProps as TableContentObject,
    HeadingContentProps as HeadingContentObject,
    DividerContentProps as DividerContentObject,
} from '../../content/item/contentItemProps'; // Corrected path

// Re-export the final view types from contentItemProps
export type {
    SubsectionItem,
    StandaloneItem,
    ProcessedSectionContentItem,
} from '../../content/item/contentItemProps'; // Corrected path

// --- Internal Helper Types (Using Props Aliases) ---
interface NumberedSubsectionItem extends BaseSubsectionItem {
    title: string | null;
}

type HelperAccumulatorItem =
    | NumberedSubsectionItem
    | BaseStandaloneItem
    | DividerContentObject;

interface ProcessingAccumulator {
    processed: HelperAccumulatorItem[];
    currentSubsectionTitle: string | null;
    currentSubsectionContent: Array<TextContentObject | TableContentObject> | null;
    previousBlockKind: PrimitiveContentBlock['kind'] | null;
    subsectionCounter: number;
    chapterNumber: number;
    sectionIndex: number;
}
// --- End Internal Helper Types ---

export function processSectionContent(
    rawContent: PrimitiveContentBlock[],
    chapterNumber: number,
    sectionIndex: number
): ProcessedSectionContentItem[] {
    const initialState: ProcessingAccumulator = {
        processed: [],
        currentSubsectionTitle: null,
        currentSubsectionContent: null,
        previousBlockKind: null,
        subsectionCounter: 0,
        chapterNumber: chapterNumber,
        sectionIndex: sectionIndex,
    };

    // Correctly type the reduce callback accumulator
    const finalAccumulator = rawContent.reduce<ProcessingAccumulator>(
        (
            acc: ProcessingAccumulator,
            block: PrimitiveContentBlock
        ): ProcessingAccumulator => {
            if (block.kind === 'heading') {
                let newProcessed = [...acc.processed];
                let nextSubsectionCounter = acc.subsectionCounter;
                if (acc.currentSubsectionContent !== null) {
                    if (
                        acc.currentSubsectionTitle ||
                        acc.currentSubsectionContent.length > 0
                    ) {
                        // const numberedTitle = acc.currentSubsectionTitle
                        //     ? `${acc.chapterNumber}.${acc.sectionIndex + 1}.${acc.subsectionCounter + 1}. ${acc.currentSubsectionTitle}`
                        //     : null;
                        const numberedTitle = acc.currentSubsectionTitle
                            ? `${acc.currentSubsectionTitle}`
                            : null;
                        const subsection: NumberedSubsectionItem = {
                            kind: 'subsection',
                            title: numberedTitle,
                            content: acc.currentSubsectionContent,
                        };
                        newProcessed.push(subsection);
                        nextSubsectionCounter++;
                    }
                }
                return {
                    ...acc,
                    processed: newProcessed,
                    currentSubsectionTitle: block.text,
                    currentSubsectionContent: [],
                    previousBlockKind: block.kind,
                    subsectionCounter: nextSubsectionCounter,
                };
            }

            if (acc.currentSubsectionContent !== null) {
                // Building a subsection
                if (block.kind === 'divider') {
                    let newProcessed = [...acc.processed];
                    let nextSubsectionCounter = acc.subsectionCounter;
                    if (
                        acc.currentSubsectionTitle ||
                        acc.currentSubsectionContent.length > 0
                    ) {
                        const numberedTitle = acc.currentSubsectionTitle
                            ? `${acc.currentSubsectionTitle}`
                            : null;
                        const subsection: NumberedSubsectionItem = {
                            kind: 'subsection',
                            title: numberedTitle,
                            content: acc.currentSubsectionContent,
                        };
                        newProcessed.push(subsection);
                        nextSubsectionCounter++;
                    }
                    const standaloneDivider: BaseStandaloneItem = {
                        kind: 'standalone',
                        content: block,
                    };
                    newProcessed.push(standaloneDivider);
                    return {
                        ...acc,
                        processed: newProcessed,
                        currentSubsectionTitle: null,
                        currentSubsectionContent: null,
                        previousBlockKind: block.kind,
                        subsectionCounter: nextSubsectionCounter,
                    };
                } else if (block.kind === 'text' || block.kind === 'table') {
                    return {
                        ...acc,
                        currentSubsectionContent: [
                            ...acc.currentSubsectionContent,
                            block,
                        ],
                        previousBlockKind: block.kind,
                    };
                }
            } else {
                // Handle as standalone
                let newProcessed = [...acc.processed];
                const standaloneItem: BaseStandaloneItem = {
                    kind: 'standalone',
                    content: block,
                };
                newProcessed.push(standaloneItem);
                return {
                    ...acc,
                    processed: newProcessed,
                    currentSubsectionTitle: null,
                    currentSubsectionContent: null,
                    previousBlockKind: block.kind,
                };
            }
            // Ensure all code paths return ProcessingAccumulator
            // This fallback should ideally not be reached if all block.kind are handled
            console.warn('Unhandled block kind in processSectionContent:', block);
            return acc;
        },
        initialState
    );

    // Finalize any remaining subsection
    let finalProcessedItems = [...finalAccumulator.processed];
    if (finalAccumulator.currentSubsectionContent !== null) {
        if (
            finalAccumulator.currentSubsectionTitle ||
            finalAccumulator.currentSubsectionContent.length > 0
        ) {
            const numberedTitle = finalAccumulator.currentSubsectionTitle
                ? `${finalAccumulator.currentSubsectionTitle}`
                : null;
            const subsection: NumberedSubsectionItem = {
                kind: 'subsection',
                title: numberedTitle,
                content: finalAccumulator.currentSubsectionContent,
            };
            finalProcessedItems.push(subsection);
        }
    }

    // Map the internal accumulator items to the final view structure
    return finalProcessedItems.map((item): ProcessedSectionContentItem => {
        if (item.kind === 'subsection') {
            // Cast to BaseSubsectionItem to match ProcessedSectionContentItem union
            return item as BaseSubsectionItem;
        } else if (item.kind === 'standalone') {
            return item;
        } else {
            // Wrap primitive DividerContentObject in StandaloneItem
            return { kind: 'standalone', content: item };
        }
    });
}
